/**
 * useDownloadRfiBundle (P177) -- POST a set of assurance question ids and
 * download the signed RFI bundle (zip). One pack answering a whole
 * examiner request. Manual bearer (mirrors useDownloadAnswerPack).
 */
import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import { currentAuthToken } from "shared/api/client";

export class RfiBundleDownloadError extends Error {
  constructor(
    public readonly status: number,
    public readonly detail: string,
  ) {
    super(detail);
    this.name = "RfiBundleDownloadError";
  }
}

async function _download(questionIds: string[]): Promise<{ filename: string }> {
  const token = currentAuthToken();
  const response = await fetch("/v1/assurance/rfi-pack.zip", {
    method: "POST",
    headers: {
      Accept: "application/zip",
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ question_ids: questionIds }),
  });
  if (!response.ok) {
    let detail = response.statusText || `HTTP ${response.status}`;
    try {
      detail = (await response.json())?.detail ?? detail;
    } catch {
      // keep statusText
    }
    throw new RfiBundleDownloadError(response.status, detail);
  }
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "marsad-rfi-bundle.zip";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
  return { filename: "marsad-rfi-bundle.zip" };
}

export function useDownloadRfiBundle(): UseMutationResult<
  { filename: string },
  RfiBundleDownloadError,
  string[]
> {
  return useMutation({ mutationFn: _download });
}
